import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Search, Eye, Pencil, Trash2, Loader2, X } from "lucide-react";

const EDIT_FIELDS = [
  ["name", "Name"], ["mobile", "Mobile"], ["email", "Email"], ["instagram", "Instagram"],
  ["youtube", "YouTube"], ["facebook", "Facebook"],
  ["vehicle_type", "Vehicle Type"], ["vehicle_brand", "Vehicle Brand"], ["vehicle_model", "Vehicle Model"],
  ["pass_type_key", "Pass Tier"], ["pass_count", "Pass Count"], ["group_size", "Group Size"],
  ["food_preference", "Food"], ["drinks_preference", "Drinks"],
  ["amount", "Amount (₹)"],
];

export default function RegistrationsTab({ regs, reload, onExport }) {
  const [search, setSearch] = useState("");
  const [passFilter, setPassFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [viewReg, setViewReg] = useState(null);
  const [editReg, setEditReg] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteReg, setDeleteReg] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return regs.filter((r) => {
      if (q) {
        const hay = `${r.name} ${r.mobile} ${r.email} ${r.id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (passFilter !== "all" && r.pass_type !== passFilter) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (dateFrom && (!r.created_at || r.created_at.slice(0, 10) < dateFrom)) return false;
      if (dateTo && (!r.created_at || r.created_at.slice(0, 10) > dateTo)) return false;
      return true;
    });
  }, [regs, search, passFilter, statusFilter, dateFrom, dateTo]);

  const openEdit = (r) => { setEditReg(r); setEditForm({ ...r }); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {};
      EDIT_FIELDS.forEach(([k]) => { payload[k] = editForm[k]; });
      payload.status = editForm.status;
      payload.competition_entry = !!editForm.competition_entry;
      await api.put(`/registrations/${editReg.id}`, payload);
      toast.success("Registration updated");
      setEditReg(null);
      reload();
    } catch (e) {
      toast.error("Failed to update registration");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/registrations/${deleteReg.id}`);
      toast.success("Registration deleted");
      setDeleteReg(null);
      reload();
    } catch (e) {
      toast.error("Failed to delete registration");
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = () => { setSearch(""); setPassFilter("all"); setStatusFilter("all"); setDateFrom(""); setDateTo(""); };

  return (
    <div className="rounded-2xl border border-border bg-[#121212] p-6 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-2xl font-800 uppercase tracking-tight">Registrations ({filtered.length})</h2>
        <Button data-testid="export-csv-button" onClick={() => onExport(filtered)} disabled={filtered.length === 0} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722]">
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 mb-5 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]" />
          <Input data-testid="reg-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, mobile, email or ID…" className="pl-9" />
        </div>
        <FilterSelect testid="reg-pass-filter" label="Pass" value={passFilter} onChange={setPassFilter} options={[["all", "All Passes"], ["day", "Day Pass"], ["meraki", "Meraki Pass"]]} />
        <FilterSelect testid="reg-status-filter" label="Status" value={statusFilter} onChange={setStatusFilter} options={[["all", "All Status"], ["paid", "Paid"], ["pending", "Pending"]]} />
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-[#71717A]">From</Label>
          <Input data-testid="reg-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wide text-[#71717A]">To</Label>
          <Input data-testid="reg-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" />
        </div>
        <Button data-testid="reg-clear-filters" onClick={clearFilters} variant="outline" size="sm" className="rounded-full border-border h-10"><X className="h-4 w-4 mr-1" /> Clear</Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table data-testid="registrations-table">
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Pass</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Attendees</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Comp.</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-[#71717A] py-8">No registrations match your filters</TableCell></TableRow>
            )}
            {filtered.map((r) => {
              const attendees = Array.isArray(r.members) ? r.members.length : (r.people_total || 0);
              const compCount = Array.isArray(r.members) ? r.members.filter((m) => m.competition_entry).length : 0;
              return (
              <TableRow key={r.id} className="border-border" data-testid={`reg-row-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell><Badge variant="outline" className="border-border capitalize">{r.pass_type}</Badge></TableCell>
                <TableCell className="text-[#A1A1AA]">{r.pass_tier || "—"}</TableCell>
                <TableCell className="text-[#A1A1AA]">{attendees}</TableCell>
                <TableCell className="text-[#A1A1AA] text-xs">{r.mobile}<br />{r.email}</TableCell>
                <TableCell>{compCount > 0 ? <Badge className="bg-[#FF4500]/20 text-[#FF7043] border-0">{compCount}</Badge> : <span className="text-[#71717A]">—</span>}</TableCell>
                <TableCell>₹{(r.amount || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell><Badge className={r.status === "paid" ? "bg-[#FF4500]/20 text-[#FF7043] border-0" : "bg-white/10 text-[#A1A1AA] border-0"}>{r.status}</Badge></TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button data-testid={`reg-view-${r.id}`} onClick={() => setViewReg(r)} variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10" title="View"><Eye className="h-4 w-4" /></Button>
                    <Button data-testid={`reg-edit-${r.id}`} onClick={() => openEdit(r)} variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10" title="Edit"><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`reg-delete-${r.id}`} onClick={() => setDeleteReg(r)} variant="ghost" size="icon" className="h-8 w-8 hover:bg-red-500/15 text-red-400" title="Delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* VIEW dialog */}
      <Dialog open={!!viewReg} onOpenChange={(o) => !o && setViewReg(null)}>
        <DialogContent data-testid="reg-view-dialog" className="bg-[#121212] border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-800 uppercase tracking-tight">Registration Details</DialogTitle></DialogHeader>
          {viewReg && (
            <div className="space-y-2 text-sm">
              {Object.entries(viewReg).filter(([k]) => !["members", "pricing_breakdown", "competition_description", "activities"].includes(k)).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border/60 py-1.5">
                  <span className="text-[#71717A] capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="text-[#E4E4E7] text-right break-all">{String(v === "" || v == null ? "—" : v)}</span>
                </div>
              ))}
              {viewReg.pricing_breakdown && (
                <div className="rounded-lg border border-border p-3">
                  <div className="text-xs uppercase tracking-wide text-[#FF7043] font-700 mb-2">Pricing Breakdown</div>
                  {Object.entries(viewReg.pricing_breakdown).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 py-0.5 text-xs">
                      <span className="text-[#71717A] capitalize">{k.replace(/_/g, " ")}</span>
                      <span className="text-[#E4E4E7]">{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(viewReg.activities) && viewReg.activities.length > 0 && (
                <div className="rounded-lg border border-border p-3" data-testid="reg-view-activities">
                  <div className="text-xs uppercase tracking-wide text-[#FF7043] font-700 mb-2">Activities</div>
                  {viewReg.activities.map((a, i) => (
                    <div key={i} className="flex justify-between gap-4 py-0.5 text-xs">
                      <span className="text-[#A1A1AA]">{a.name} × {a.qty}</span>
                      <span className="text-[#E4E4E7]">₹{a.subtotal}</span>
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(viewReg.members) && viewReg.members.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-[#FF7043] font-700 pt-1">Attendees ({viewReg.members.length})</div>
                  {viewReg.members.map((m, i) => (
                    <div key={i} className="rounded-lg border border-border p-3 text-xs space-y-1">
                      <div className="font-semibold text-white">Attendee {i + 1}: {m.name || "—"} · {m.mobile || "—"}</div>
                      <div className="text-[#A1A1AA]">Vehicle: {m.vehicle_type || "—"} {m.vehicle_brand} {m.vehicle_model}</div>
                      <div className="text-[#A1A1AA]">T-shirt: {m.tshirt_size || "—"}</div>
                      <div className="text-[#A1A1AA]">Bike competition: {m.competition_entry ? "Yes" : "No"}{m.competition_entry && m.competition_description ? ` — ${m.competition_description}` : ""}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* EDIT dialog */}
      <Dialog open={!!editReg} onOpenChange={(o) => !o && setEditReg(null)}>
        <DialogContent data-testid="reg-edit-dialog" className="bg-[#121212] border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-800 uppercase tracking-tight">Edit Registration</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {EDIT_FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wide text-[#71717A]">{label}</Label>
                <Input data-testid={`reg-edit-${k}`} value={editForm[k] ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wide text-[#71717A]">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="reg-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-border">
                  <SelectItem value="pending">pending</SelectItem>
                  <SelectItem value="paid">paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="rounded-full border-border" onClick={() => setEditReg(null)}>Cancel</Button>
            <Button data-testid="reg-edit-save" onClick={saveEdit} disabled={saving} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DELETE confirm */}
      <AlertDialog open={!!deleteReg} onOpenChange={(o) => !o && setDeleteReg(null)}>
        <AlertDialogContent data-testid="reg-delete-dialog" className="bg-[#121212] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete registration?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              This will permanently remove <span className="text-white font-semibold">{deleteReg?.name}</span>'s {deleteReg?.pass_type} registration. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="reg-delete-confirm" onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} className="rounded-full bg-red-600 hover:bg-red-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-wide text-[#71717A]">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="w-[150px]"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#1A1A1A] border-border">
          {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
